import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { haptic } from "@/lib/telegram";
import { cn } from "@/lib/cn";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: "auto" | "full";
}

export function Sheet({ open, onClose, title, description, children, size = "auto" }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    haptic.impact("light");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            className={cn(
              "fixed inset-x-0 bottom-0 z-50",
              "glass-strong rounded-t-[28px] overflow-hidden",
              "border-t border-white/[0.08] flex flex-col min-h-0",
              size === "full" ? "top-4 sm:top-12" : "max-h-[92dvh]",
            )}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) onClose();
            }}
          >
            <div className="flex items-center justify-center pt-3 pb-1 shrink-0">
              <div className="w-10 h-1 rounded-full bg-white/15" />
            </div>
            {(title || description) && (
              <div className="px-4 sm:px-6 pt-3 pb-4 flex items-start justify-between gap-4 shrink-0">
                <div className="flex-1">
                  {title && <div className="text-xl font-semibold tracking-tight">{title}</div>}
                  {description && (
                    <div className="text-sm text-text-muted mt-1">{description}</div>
                  )}
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="size-9 grid place-items-center rounded-full bg-white/[0.06] hover:bg-white/[0.1] transition-colors"
                >
                  <X className="size-4" />
                </button>
              </div>
            )}
            <div className="px-4 sm:px-6 pb-8 overflow-y-auto flex-1 min-h-0">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
